import { ENGINE_PROVIDER } from "@/lib/engine";
import { useUiStore } from "@/stores/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { BookOpen, Check, X } from "lucide-react";
import { StrategyLibraryDialog } from "./StrategyLibraryDialog";
import type { LibraryStrategy } from "@/lib/strategy-library";
import type { MarketSymbol, ParamSpec } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { DynamicForm, buildConfigPayload, fillParamDefaults } from "../DynamicForm";
import { DateTimePicker } from "../DateTimePicker";
import { ScriptEditor } from "../ScriptEditor";
import { SymbolMarketSelect } from "../SymbolMarketSelect";
import { providerLabel } from "@/lib/account";
import { toDateTime } from "@/lib/backtest-run";
import { normalizeConfigurableParams } from "@/lib/configurable-params";
import {
  isMarketProviderEnabled,
  marketVariants,
  sortMarketProviders,
  type MarketListing,
  type MarketTradeType,
} from "@/lib/market-provider";
import {
  HISTORY_PRESETS,
  declaresStrategy,
  defaultBacktestName,
  estimateCandles,
  historyStart,
  newBacktestEngineConfig,
  pineStrategyName,
  type HistoryPreset,
} from "@/lib/new-backtest";
import {
  marginToLeverageText,
  orderSizeText,
  presentTradingViewEngineExtras,
  presentTradingViewEngineProperties,
  stripUiOnlyEngineFields,
  tradingViewEnginePropertyDefaults,
} from "@/lib/engine-properties";
import { resolveRecalculate } from "@/lib/recalculate";
import { formatScriptRef } from "@/lib/script-ref";
import { fetchStrategyInfo, suggestedEngineConfig, type StrategyInfoResponse } from "@/lib/strategy-info";
import { saveValidatedScript } from "@/lib/script-save";
import { buildEngineSaveConfig, buildFormStrategyConfig, fetchPineStrategySpecs, parsePineStrategyProps } from "@/lib/strategy-leverage";
import { cn } from "@/lib/utils";

type Check =
  | { state: "empty" }
  | { state: "checking" }
  | { state: "invalid"; error: string }
  | { state: "valid"; name: string | null; inputs: number };

const FALLBACK_TIMEFRAMES = ["1m", "5m", "15m", "1h", "4h", "1d"];
/** Shown as chips; anything else the exchange offers goes under "More". */
const COMMON_TIMEFRAMES = FALLBACK_TIMEFRAMES;
const STEP_HINT = {
  1: "Every backtest keeps its own copy of the script.",
  2: "Symbol, exchange and dates can be changed later.",
  3: "Creates the backtest and runs it. You land in the workspace with the results.",
} as const;

const chip = (on: boolean) =>
  cn(
    "num rounded-full border px-3 py-1 text-[13px] transition-colors",
    on
      ? "border-[color-mix(in_srgb,var(--primary)_60%,var(--border))] bg-[var(--bg-card-elevated)] text-[var(--text-primary)]"
      : "border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
  );
const label = "text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--text-muted)]";
const fieldInput =
  "!h-auto !min-h-0 w-full !rounded-[10px] !border !border-[var(--border)] !bg-[var(--bg-secondary)] !px-2.5 !py-1.5 text-[13px] text-[var(--text-primary)]";

/** 3-step “New backtest” dialog: Pine Script → market → preview. */
export default function NewBacktestModal({ onClose }: { onClose: () => void }) {
  const api = useApi([]);
  const request = api.commands;
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [libraryOpen, setLibraryOpen] = useState(false);
  /** The library strategy the script came from, while the script is still its own. */
  const [fromLibrary, setFromLibrary] = useState<{ name: string; source: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // ── Step 1: script ─────────────────────────────────────────
  const [source, setSource] = useState(() => useUiStore.getState().newBacktestSource);
  const [check, setCheck] = useState<Check>({ state: "empty" });
  /** The script's input.* declarations, shown under Inputs on the last step. */
  const [strategySpecs, setStrategySpecs] = useState<ParamSpec[]>([]);
  const [inputValues, setInputValues] = useState<Record<string, unknown>>({});
  /** The server's reading of strategy(): the properties TradingView would run it with. */
  const [strategyInfo, setStrategyInfo] = useState<StrategyInfoResponse | null>(null);
  useEffect(() => {
    const src = source.trim();
    if (!src) {
      setCheck({ state: "empty" });
      return;
    }
    if (!declaresStrategy(src)) {
      setCheck({ state: "invalid", error: "This script has no strategy() call. Backtests need a strategy, not an indicator." });
      return;
    }
    setCheck({ state: "checking" });
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const v = (await request.validateConfigurable({
          category: "strategy",
          name: "pinescript",
          config: { source: src },
        })) as { valid?: boolean; error?: string } | null;
        if (cancelled) return;
        if (v && v.valid === false) {
          setCheck({ state: "invalid", error: v.error || "This isn't a valid Pine strategy." });
          return;
        }
        const [specs, info] = await Promise.all([fetchPineStrategySpecs(request, src), fetchStrategyInfo(request, src)]);
        if (cancelled) return;
        setStrategySpecs(specs);
        setStrategyInfo(info);
        // Keep what the user typed for inputs the edited script still has.
        setInputValues((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => specs.some((sp) => sp.name === k))));
        setCheck({
          state: "valid",
          name: pineStrategyName(src),
          inputs: specs.filter((s) => s.name !== "source" && s.type !== "script").length,
        });
      } catch (e: unknown) {
        if (!cancelled) setCheck({ state: "invalid", error: e instanceof Error ? e.message : "Couldn't check the script." });
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [source, request]);

  // ── Step 2: exchange, market, history ──────────────────────
  const [providers, setProviders] = useState<string[]>(["binance", "mexc", "revolutx"]);
  const [provider, setProvider] = useState("binance");
  const [symbol, setSymbol] = useState("");
  /** Spot or futures of the chosen symbol, from the market it was listed on. */
  const [market, setMarket] = useState<MarketTradeType>("futures");
  /** False while the symbol is still our BTC default: the field asks the user to choose. */
  const [symbolChosen, setSymbolChosen] = useState(false);
  const [timeframes, setTimeframes] = useState<string[]>(FALLBACK_TIMEFRAMES);
  const [timeframe, setTimeframe] = useState("1h");
  const tfChips = COMMON_TIMEFRAMES.filter((tf) => timeframes.includes(tf));
  const tfMore = timeframes.filter((tf) => !tfChips.includes(tf));
  const [preset, setPreset] = useState<HistoryPreset>("1Y");
  const [customStart, setCustomStart] = useState(() => historyStart("1Y"));
  const [customEnd, setCustomEnd] = useState("");
  /** One market config per market (spot/futures) each exchange offers. */
  const marketConfigs = useRef<Record<string, { market: MarketTradeType; config: Record<string, unknown> }[]>>({});

  useEffect(() => {
    request.listConfigurables()
      .then((list: { market_providers?: string[] } | null) => {
        const mps = (list?.market_providers || []).filter(isMarketProviderEnabled);
        if (mps.length) setProviders(sortMarketProviders(mps));
      })
      .catch(() => {});
  }, [request]);

  const symbolCache = useRef<Record<string, MarketListing[]>>({});

  async function marketConfigsFor(prov: string) {
    let variants = marketConfigs.current[prov];
    if (!variants) {
      try {
        const raw = await request.getParams({ category: "market_provider", name: prov, config: {} });
        const specs = normalizeConfigurableParams(raw).specs;
        variants = marketVariants(buildConfigPayload(fillParamDefaults({}, specs), specs), specs);
      } catch {
        variants = [{ market: "spot", config: {} }];
      }
      marketConfigs.current[prov] = variants;
    }
    return variants;
  }

  /** Market config of one market of an exchange (its first market when it has no such one). */
  async function marketConfigFor(prov: string, m: MarketTradeType): Promise<Record<string, unknown>> {
    const variants = await marketConfigsFor(prov);
    return (variants.find((v) => v.market === m) ?? variants[0])?.config ?? {};
  }

  /** Every symbol an exchange lists, on each of its markets; the picker filters spot/futures itself. */
  async function loadSymbols(prov: string): Promise<MarketListing[]> {
    if (symbolCache.current[prov]) return symbolCache.current[prov]!;
    const variants = await marketConfigsFor(prov);
    const lists = await Promise.all(
      variants.map(async ({ market: m, config }) => {
        const syms = await request.getMarketSymbols({ market_provider: prov, market_config: config });
        return (Array.isArray(syms) ? (syms as MarketSymbol[]) : [])
          .map((x) => x.name)
          .filter(Boolean)
          .map((name): MarketListing => ({ symbol: name, market: m }));
      })
    );
    const listings = lists.flat();
    symbolCache.current[prov] = listings;
    return listings;
  }

  // Start on BTC perpetual futures of the first exchange (else BTC spot, else its first symbol).
  useEffect(() => {
    let cancelled = false;
    loadSymbols(provider)
      .then((listings) => {
        if (cancelled || symbol) return;
        const btc = (l: MarketListing) => l.symbol.startsWith("BTC");
        const pick =
          listings.find((l) => btc(l) && l.market === "futures") || listings.find(btc) || listings[0];
        if (!pick) return;
        setSymbol(pick.symbol);
        setMarket(pick.market);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once
  }, []);

  // Timeframes of the chosen exchange.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await marketConfigFor(provider, market);
      const tfs = await request.getMarketTimeframes({ market_provider: provider, market_config: cfg }).catch(() => null);
      if (cancelled) return;
      const list = Array.isArray(tfs) && tfs.length ? (tfs as string[]) : FALLBACK_TIMEFRAMES;
      setTimeframes(list);
      setTimeframe((cur) => (list.includes(cur) ? cur : list.includes("1h") ? "1h" : list[0] || cur));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, market, request]);

  const startDate = preset === "custom" ? customStart : historyStart(preset);
  const endDate = preset === "custom" && customEnd ? customEnd : null;
  const candles = estimateCandles(startDate, endDate, timeframe);
  const exchangeName = providerLabel(provider);
  const marketDetail = `${exchangeName} ${market === "futures" ? "Futures" : "Spot"}`;
  const marketLabel = `${symbol || "—"} · ${marketDetail} · ${timeframe}`;

  // ── Step 3: name and settings ──────────────────────────────
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const autoName = defaultBacktestName(check.state === "valid" ? check.name : null);
  useEffect(() => {
    if (!nameTouched) setName(autoName);
  }, [autoName, nameTouched]);

  // One engine for spot and futures: spot simply runs with 100% margin (no leverage).
  const engineKind = ENGINE_PROVIDER;
  const [engineSpecs, setEngineSpecs] = useState<ParamSpec[]>([]);
  useEffect(() => {
    let cancelled = false;
    request.getParams({ category: "engine", name: engineKind, config: {} })
      .then((p: unknown) => {
        if (!cancelled) setEngineSpecs(normalizeConfigurableParams(p).specs);
      })
      .catch(() => {
        if (!cancelled) setEngineSpecs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [engineKind, request]);

  const base = useMemo(
    () => newBacktestEngineConfig(engineKind, engineSpecs, source, suggestedEngineConfig(strategyInfo, engineSpecs, engineKind)),
    [engineKind, engineSpecs, source, strategyInfo]
  );
  /** Properties the user changed on the last step; everything else follows the defaults above. */
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});
  useEffect(() => setOverrides({}), [engineKind]);
  const engineValues = useMemo(() => ({ ...base.config, ...overrides }), [base.config, overrides]);
  const propertySpecs = useMemo(() => presentTradingViewEngineProperties(engineSpecs), [engineSpecs]);
  const extrasSpecs = useMemo(() => presentTradingViewEngineExtras(engineSpecs), [engineSpecs]);
  const formValues = (specs: ParamSpec[]) => ({
    ...tradingViewEnginePropertyDefaults(specs),
    ...fillParamDefaults(engineValues, engineSpecs),
  });
  /** Keep only real engine fields the form changed (it also emits UI-only helpers). */
  const applyEngineForm = (next: Record<string, unknown>, specs: ParamSpec[]) => {
    const cleaned = stripUiOnlyEngineFields(next);
    setOverrides((prev) => {
      const out = { ...prev };
      for (const sp of specs) {
        if (sp.disabled || cleaned[sp.name] === undefined) continue;
        if (cleaned[sp.name] !== engineValues[sp.name]) out[sp.name] = cleaned[sp.name];
      }
      return out;
    });
  };
  const capitalKey = "initial";
  const props = parsePineStrategyProps(source);
  const settingsSummary = [
    `${Number(engineValues[capitalKey] || 0).toLocaleString("en-US")} USDT${props.initial != null && !(capitalKey in overrides) ? " (from script)" : ""}`,
    orderSizeText(engineValues.qty, engineValues.qty_unit),
    `fee ${engineValues.fee}${(engineValues.fee_unit || "percent") === "percent" ? "%" : ` USD ${String(engineValues.fee_unit).replace("_", " ")}`}`,
    `leverage ${marginToLeverageText(engineValues.margin_long)}`,
  ].join(" · ");

  const inputSpecs = useMemo(() => strategySpecs.filter((sp) => sp.name !== "source" && sp.type !== "script"), [strategySpecs]);
  const inputFormValues = useMemo(() => fillParamDefaults(inputValues, inputSpecs), [inputValues, inputSpecs]);
  const changedInputs = Object.keys(inputValues).length;

  // ── Navigation ─────────────────────────────────────────────
  const canNext =
    step === 1 ? check.state === "valid" : step === 2 ? !!symbol && !!timeframe && !!startDate : name.trim().length > 0;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // The symbol picker opens its own dialog on top; let it handle its Escape.
      if (e.key !== "Escape" || creating) return;
      const active = document.activeElement;
      if (active && active !== document.body && !dialogRef.current?.contains(active)) return;
      onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [creating, onClose]);

  async function create() {
    setCreating(true);
    setError(null);
    try {
      const saved = await saveValidatedScript(request, { name: name.trim(), source });
      const specs = await fetchPineStrategySpecs(request, saved.source);
      const typed = buildConfigPayload(fillParamDefaults({ ...inputValues, source: saved.source }, specs), specs);
      const { source: _drop, ...inputs } = buildFormStrategyConfig({ ...typed, source: saved.source }, specs);
      const strategy_config = { ...inputs, source: formatScriptRef(saved.id, saved.rev_no >= 1 ? saved.rev_no : 1) };

      const engine_config = buildEngineSaveConfig(engineValues, engineSpecs, engineKind, buildConfigPayload, fillParamDefaults);

      const recalculate = await resolveRecalculate(request, "pinescript", strategy_config);

      const created = await request.createBacktest({
        name: name.trim(),
        description: "",
        market_provider: provider,
        market_config: await marketConfigFor(provider, market),
        symbol,
        timeframe,
        strategy_provider: "pinescript",
        strategy_config,
        engine_provider: engineKind,
        engine_config,
        start_date: toDateTime(startDate),
        ...(endDate ? { end_date: toDateTime(endDate) } : {}),
        ...(recalculate ? { recalculate } : {}),
        metadata: { ui: { mode: "lab", script_id: saved.id } },
        enabled: false,
      });
      onClose();
      navigate(`/lab/backtest/${created.id}`, { state: { syncedBacktest: created, autoRun: true } });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Couldn't create the backtest. Try again.");
      setCreating(false);
    }
  }

  function next() {
    if (!canNext) return;
    if (step < 3) setStep((step + 1) as 2 | 3);
    else void create();
  }

  const scriptSummary =
    check.state === "valid"
      ? `“${check.name || "Strategy"}” · ${check.inputs} ${check.inputs === 1 ? "input" : "inputs"} · ${source.trim().split("\n").length} lines`
      : "";

  const nameField = (
    <label className="flex flex-col gap-1.5">
      <span className={label}>Name</span>
      <input
        id={`nbt-name-${step}`}
        value={name}
        placeholder="Backtest name"
        onChange={(e) => {
          setNameTouched(true);
          setName(e.target.value);
        }}
        className={fieldInput}
      />
    </label>
  );

  // A library strategy brings its script and the market it is built for.
  function pickFromLibrary(strategy: LibraryStrategy, script: string) {
    setSource(script);
    setFromLibrary({ name: strategy.name, source: script });
    const b = strategy.builtFor;
    setProvider(b.provider);
    setMarket(b.market);
    setSymbol(b.symbol);
    setSymbolChosen(true);
    setTimeframe(b.timeframe);
    setLibraryOpen(false);
  }

  return createPortal(
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onMouseDown={() => !creating && onClose()}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="nbt-title"
        className="flex h-[min(700px,calc(100dvh-2rem))] w-full max-w-[760px] flex-col overflow-hidden rounded-[16px] border border-[var(--border)] bg-[var(--bg-card)] shadow-lg"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-[var(--border)] px-5 py-4">
          <h2 id="nbt-title" className="m-0 text-base font-semibold text-[var(--text-primary)]">
            New backtest
          </h2>
          <button
            type="button"
            aria-label="Close"
            disabled={creating}
            onClick={onClose}
            className="ml-auto rounded-md p-1 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
          >
            <X size={18} />
          </button>
        </div>

        <ol className="m-0 flex list-none flex-wrap items-center gap-1 border-b border-[var(--divider)] px-5 py-3">
          {(
            [
              [1, "Pine Script", step > 1 ? check.state === "valid" && check.name : ""],
              [2, "Symbol & timeframe", step > 2 ? `${symbol} · ${exchangeName}` : ""],
              [3, "Preview", ""],
            ] as const
          ).map(([n, title, sum], i) => {
            const done = n < step;
            const on = n === step;
            return (
              <li key={n} className="flex items-center gap-1">
                {i > 0 && <span className="mx-1 h-px w-3.5 bg-[var(--border)]" aria-hidden />}
                <button
                  type="button"
                  disabled={!done || creating}
                  onClick={() => setStep(n)}
                  aria-current={on ? "step" : undefined}
                  className={cn(
                    "flex items-center gap-2 rounded-full py-1 pl-1 pr-2.5 text-[13px] disabled:cursor-default",
                    on ? "bg-[var(--primary-soft)] text-[var(--text-primary)]" : "text-[var(--text-muted)]"
                  )}
                >
                  <span
                    className={cn(
                      "num grid h-[22px] w-[22px] place-items-center rounded-full text-[11px] font-semibold",
                      on
                        ? "bg-[var(--primary)] text-[var(--on-primary)]"
                        : done
                          ? "bg-[var(--success-soft)] text-[var(--success)]"
                          : "bg-[var(--bg-hover)]"
                    )}
                  >
                    {done ? <Check size={12} /> : n}
                  </span>
                  {title}
                  {sum && <span className="text-xs text-[var(--text-disabled)]">· {sum}</span>}
                </button>
              </li>
            );
          })}
        </ol>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-[18px] [&>*]:shrink-0">
          {step === 1 && (
            <>
              {nameField}
              <button
                type="button"
                onClick={() => setLibraryOpen(true)}
                className="flex items-center gap-3 rounded-[12px] border border-[color-mix(in_srgb,var(--primary)_45%,var(--border))] bg-[var(--primary-soft)] px-3.5 py-3 text-left hover:border-[var(--primary)]"
              >
                <BookOpen size={20} className="shrink-0 text-[var(--primary)]" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-semibold text-[var(--text-primary)]">
                    {fromLibrary && fromLibrary.source === source ? `From the library: ${fromLibrary.name}` : "Start from the library"}
                  </span>
                  <span className="block text-xs text-[var(--text-muted)]">
                    Ready strategies with their results by year. Or paste your own TradingView strategy below; we validate it as you type.
                  </span>
                </span>
                <span className="btn btn-primary btn-sm shrink-0">Browse</span>
              </button>
              <div className="overflow-hidden rounded-[12px] border border-[var(--border)]">
                <ScriptEditor value={source} onChange={setSource} height={280} />
              </div>
              {check.state !== "empty" && (
                <div
                  role="status"
                  className={cn(
                    "flex flex-wrap gap-x-3.5 gap-y-1 rounded-[10px] border px-3 py-2.5 text-xs",
                    check.state === "invalid"
                      ? "border-[color-mix(in_srgb,var(--danger)_40%,var(--border))] bg-[var(--danger-bg)] text-[var(--danger)]"
                      : "border-[var(--divider)] bg-[var(--bg-card-elevated)] text-[var(--text-muted)]"
                  )}
                >
                  {check.state === "checking" && "Checking…"}
                  {check.state === "invalid" && <span className="whitespace-pre-wrap">{check.error}</span>}
                  {check.state === "valid" && (
                    <>
                      <span className="text-[var(--success)]">✓ Valid strategy</span>
                      {check.name && (
                        <span>
                          <span className="num text-[var(--text-primary)]">strategy()</span> “{check.name}”
                        </span>
                      )}
                      <span>
                        <b className="font-medium text-[var(--text-primary)]">{check.inputs}</b> inputs
                      </span>
                      {props.initial != null && (
                        <span>
                          Capital <b className="num font-medium text-[var(--text-primary)]">{props.initial.toLocaleString("en-US")}</b>
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="flex flex-col gap-1.5">
                <span className={label}>Symbol</span>
                <SymbolMarketSelect
                  variant="big"
                  symbol={symbol}
                  provider={provider}
                  market={symbol ? market : undefined}
                  attention={!symbolChosen}
                  providers={providers}
                  loadSymbols={loadSymbols}
                  onChange={(next) => {
                    setProvider(next.provider);
                    setSymbol(next.symbol);
                    setMarket(next.market);
                    setSymbolChosen(true);
                  }}
                  placeholder="Choose a symbol"
                  searchPlaceholder="Search a symbol, e.g. ETH"
                  title="Choose symbol"
                  detail={marketDetail}
                />
                <span className="text-xs text-[var(--text-muted)]">
                  The backtest runs on this exchange's candles. The same coin can trade at slightly different prices on each exchange.
                </span>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className={label}>Timeframe</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {tfChips.map((tf) => (
                    <button key={tf} type="button" aria-pressed={timeframe === tf} onClick={() => setTimeframe(tf)} className={chip(timeframe === tf)}>
                      {tf}
                    </button>
                  ))}
                  {tfMore.length > 0 && (
                    <select
                      aria-label="More timeframes"
                      value={tfMore.includes(timeframe) ? timeframe : ""}
                      onChange={(e) => e.target.value && setTimeframe(e.target.value)}
                      className={cn(
                        "num !h-auto !min-h-0 !w-auto rounded-full !py-1 !pl-3 !pr-7 !text-[13px]",
                        tfMore.includes(timeframe)
                          ? "!border-[color-mix(in_srgb,var(--primary)_60%,var(--border))] text-[var(--text-primary)]"
                          : "text-[var(--text-muted)]"
                      )}
                    >
                      <option value="">More</option>
                      {tfMore.map((tf) => (
                        <option key={tf} value={tf}>
                          {tf}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className={label}>History</span>
                <div className="flex flex-wrap gap-1.5">
                  {HISTORY_PRESETS.map((p) => (
                    <button key={p.id} type="button" aria-pressed={preset === p.id} onClick={() => setPreset(p.id)} className={chip(preset === p.id)}>
                      {p.label}
                    </button>
                  ))}
                </div>
                {preset === "custom" && (
                  <div className="mt-1 grid gap-3 sm:grid-cols-2">
                    <label className="flex flex-col gap-1">
                      <span className={label}>From</span>
                      <DateTimePicker value={customStart} onChange={setCustomStart} max={customEnd || undefined} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className={label}>To</span>
                      <DateTimePicker value={customEnd} onChange={setCustomEnd} min={customStart} clearable />
                      <span className="text-xs text-[var(--text-muted)]">Leave empty to run up to now.</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 rounded-[10px] bg-[var(--primary-soft)] px-3 py-2.5 text-[13px] text-[var(--text-secondary)]">
                <span aria-hidden>⚡</span>
                <span>
                  {candles != null ? (
                    <>
                      About <b className="num font-semibold text-[var(--primary)]">{candles.toLocaleString("en-US")}</b> candles ·{" "}
                    </>
                  ) : null}
                  <span className="num">{startDate.slice(0, 10)}</span> → {endDate ? <span className="num">{endDate.slice(0, 10)}</span> : "now"}
                </span>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              {nameField}

              <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-[12px] border border-[var(--border)] text-[13px]">
                {(
                  [
                    ["Pine Script", scriptSummary, 1, "Edit"],
                    ["Symbol", marketLabel, 2, "Change"],
                    [
                      "History",
                      `${startDate.slice(0, 10)} → ${endDate ? endDate.slice(0, 10) : "now"}${candles != null ? ` · about ${candles.toLocaleString("en-US")} candles` : ""}`,
                      2,
                      "Change",
                    ],
                  ] as const
                ).map(([k, v, s, action], i) => (
                  <div key={k} className="contents">
                    <div className={cn("px-3 py-2.5 text-[var(--text-muted)]", i > 0 && "border-t border-[var(--divider)]")}>{k}</div>
                    <div className={cn("min-w-0 truncate px-3 py-2.5 text-[var(--text-primary)]", i > 0 && "border-t border-[var(--divider)]")}>{v}</div>
                    <div className={cn("px-3 py-2.5 text-right", i > 0 && "border-t border-[var(--divider)]")}>
                      <button type="button" onClick={() => setStep(s)} className="text-xs text-[var(--primary)]">
                        {action}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <details className="group rounded-[12px] border border-[var(--border)]">
                <summary className="flex cursor-pointer list-none items-center gap-2.5 px-3.5 py-3 text-[13px] [&::-webkit-details-marker]:hidden">
                  <b className="font-semibold text-[var(--text-primary)]">Inputs</b>
                  <span className="min-w-0 flex-1 truncate text-xs text-[var(--text-muted)]">
                    {inputSpecs.length
                      ? `${inputSpecs.length} from the script${changedInputs ? ` · ${changedInputs} changed` : " · using their defaults"}`
                      : "This script has no inputs"}
                  </span>
                  <span className="text-[var(--text-disabled)] transition-transform group-open:rotate-90" aria-hidden>
                    ›
                  </span>
                </summary>
                <div className="border-t border-[var(--divider)] px-3.5 py-3">
                  {inputSpecs.length ? (
                    <DynamicForm
                      specs={inputSpecs}
                      values={inputFormValues}
                      onChange={(next) => {
                        const changed: Record<string, unknown> = {};
                        for (const sp of inputSpecs) {
                          if (next[sp.name] !== undefined && next[sp.name] !== sp.default) changed[sp.name] = next[sp.name];
                        }
                        setInputValues(changed);
                      }}
                      layout="rows"
                      density="compact"
                      columns={1}
                    />
                  ) : (
                    <p className="m-0 text-xs text-[var(--text-muted)]">
                      Inputs appear here when the script declares them with <span className="num">input.*</span>.
                    </p>
                  )}
                </div>
              </details>

              <details className="group rounded-[12px] border border-[var(--border)]">
                <summary className="flex cursor-pointer list-none items-center gap-2.5 px-3.5 py-3 text-[13px] [&::-webkit-details-marker]:hidden">
                  <b className="font-semibold text-[var(--text-primary)]">Settings</b>
                  <span className="min-w-0 flex-1 truncate text-xs text-[var(--text-muted)]">{strategyInfo ? "As on TradingView" : "TradingView defaults"}: {settingsSummary}</span>
                  <span className="text-[var(--text-disabled)] transition-transform group-open:rotate-90" aria-hidden>
                    ›
                  </span>
                </summary>
                <div className="flex flex-col gap-3 border-t border-[var(--divider)] px-3.5 py-3">
                  <p className="m-0 text-xs text-[var(--text-muted)]">
                    {strategyInfo ? (
                      <>
                        The properties TradingView runs this script with: what its <span className="num">strategy()</span> declares, and Pine's
                        defaults for the rest.
                      </>
                    ) : (
                      <>
                        TradingView's defaults with a 0.1% commission. Values set in the script's <span className="num">strategy()</span> win, as
                        in TradingView.
                      </>
                    )}
                  </p>
                  <DynamicForm
                    specs={propertySpecs}
                    values={formValues(propertySpecs)}
                    onChange={(next) => applyEngineForm(next, propertySpecs)}
                    layout="rows"
                    density="compact"
                    columns={1}
                  />
                  {extrasSpecs.length > 0 && (
                    <DynamicForm
                      specs={extrasSpecs}
                      values={formValues(extrasSpecs)}
                      onChange={(next) => applyEngineForm(next, extrasSpecs)}
                      layout="rows"
                      density="compact"
                      columns={1}
                    />
                  )}
                </div>
              </details>
            </>
          )}
        </div>

        {error && (
          <div role="alert" className="mx-5 mb-3 rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">
            {error}
          </div>
        )}

        <div className="flex items-center gap-2.5 border-t border-[var(--border)] bg-[var(--bg-card-elevated)] px-5 py-3.5">
          <span className="min-w-0 flex-1 text-xs text-[var(--text-muted)]">{STEP_HINT[step]}</span>
          {step > 1 && (
            <button
              type="button"
              disabled={creating}
              onClick={() => setStep((step - 1) as 1 | 2)}
              className="btn btn-ghost"
            >
              Back
            </button>
          )}
          <button
            type="button"
            disabled={!canNext || creating}
            onClick={next}
            className="btn btn-primary"
          >
            {step < 3 ? "Continue" : creating ? "Creating…" : "Create & run"}
          </button>
        </div>
      </div>
    </div>
    {/* Beside the dialog, not in it: a click on its backdrop must not close New backtest. */}
    {libraryOpen && <StrategyLibraryDialog onPick={pickFromLibrary} onClose={() => setLibraryOpen(false)} />}
    </>,
    document.body
  );
}
